import { useEffectOnce } from 'hooks/useEffectOnce'
import { useUnmount } from 'hooks/useUnmount'
import { useCallback, useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { IMetricScope, IMetricType } from 'types/metric.types'
import { useAppDispatch } from '../../store/hooks'
import { metricSetUuid, selectMetricIds } from '../../store/slice/metric.slice'
import { handleBlurOnIframe } from './Metric.utils'
import { MetricTimer, MetricTimerStatus } from './MetricTimer'

export function useMetric({
  entityId,
  type,
  scope,
  onSave,
  isImmediateStart = true,
  hasIdleTimer = false,
  idleLimitMin = 2,
}: {
  scope?: IMetricScope
  entityId?: number
  type?: IMetricType
  onSave: (value: number, uuid: string, scope: IMetricScope, isDestroyed: boolean) => void
  isImmediateStart?: boolean
  hasIdleTimer?: boolean
  idleLimitMin?: number
}) {
  const dispatch = useAppDispatch()

  const savedMetricIds = useSelector(selectMetricIds(type))
  const metricTimer = useRef<MetricTimer>()

  const handleTimerStart = useCallback(() => {
    metricTimer.current?.start()
  }, [])

  const handleSave = useCallback(
    (isDestroyed: boolean | undefined = false) => {
      if (!metricTimer.current || !savedMetricIds?.uuid || !scope) {
        return
      }

      onSave(metricTimer.current.dateFrom, savedMetricIds.uuid, scope, !!isDestroyed)
    },
    [onSave, savedMetricIds, scope],
  )

  const handleSaveAndDestroy = useCallback(() => {
    if (!metricTimer.current) {
      return
    }

    if (metricTimer.current.status === MetricTimerStatus.InProgress) {
      handleSave(true)
    }
    metricTimer.current.destroy()
  }, [handleSave])

  const handleVisibilityChange = useCallback(() => {
    if (document.hidden) {
      handleSaveAndDestroy()
      return
    }

    metricTimer.current?.restart()
  }, [handleSaveAndDestroy])

  const handleFocus = useCallback(() => {
    // if we were in iframe like YouTube and return to main window,
    // we shouldn't restart session
    if (metricTimer.current?.status !== MetricTimerStatus.Ready) {
      return
    }

    metricTimer.current?.restart()
  }, [])

  const handleBlur = useCallback(() => {
    handleBlurOnIframe(() => {
      handleSaveAndDestroy()
    })
  }, [handleSaveAndDestroy])

  useEffectOnce(() => {
    if (entityId === undefined || type === undefined || metricTimer.current) {
      return
    }

    metricTimer.current = new MetricTimer({
      hasIdleTimer,
      idleLimitMin,
      onReady: handleSave,
    })

    if (isImmediateStart) {
      metricTimer.current?.start()
    }
  })

  useEffect(() => {
    if (entityId === undefined || type === undefined) {
      return
    }

    if (savedMetricIds?.id !== entityId) {
      dispatch(metricSetUuid({ type, id: entityId }))
    }
  }, [dispatch, entityId, savedMetricIds, type])

  useEffect(() => {
    metricTimer.current?.setReadyCallback(handleSave)
  }, [handleSave])

  // save on destroy
  useUnmount(handleSaveAndDestroy)

  // add unload handlers
  useEffect(() => {
    window.addEventListener('unload', handleSaveAndDestroy)

    return () => {
      window.removeEventListener('unload', handleSaveAndDestroy)
    }
  }, [handleSaveAndDestroy])

  useEffect(() => {
    window.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      window.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [handleVisibilityChange])

  useEffect(() => {
    window.addEventListener('focus', handleFocus)
    window.addEventListener('blur', handleBlur)

    return () => {
      window.removeEventListener('focus', handleFocus)
      window.removeEventListener('blur', handleBlur)
    }
  }, [handleBlur, handleFocus])

  return {
    onMetricTimerStart: handleTimerStart,
    onMetricSave: handleSave,
  }
}
